import { useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import RevolutCheckout from '@revolut/checkout';
import {useIsSandboxMode} from '@/lib/hooks/useIsSandboxMode.js';

export const useRevolut = (
    config,
    publicToken,
    onPaymentResult,
    root,
    upsellRoot,
    process,
    checkFinalStatus,
    successCallbackUrl,
    failureCallbackUrl,
    lineItemName,
) => {
    const { i18n: { language: locale } } = useTranslation();
    const isSandbox = useIsSandboxMode();
    const [isProcessing, setIsProcessing] = useState(false);
    const [revolutPay, setRevolutPay] = useState();
    const [revolutPayUpsell, setRevolutPayUpsell] = useState();
    const [setupError, setSetupError] = useState();

    useEffect(
        () => {
            RevolutCheckout.payments({
                mode: isSandbox ? 'sandbox' : 'prod',
                locale,
                publicToken,
            })
                .then(({ revolutPay }) => setRevolutPay(revolutPay))
                .catch((e) => setSetupError(e.message));
        },
        [locale, isSandbox, setRevolutPay, setSetupError, publicToken],
    );

    useEffect(
        () => {
            if (!revolutPay) return;

            revolutPay.on('payment', (event) => {
                switch (event.type) {
                    case 'cancel': {
                        console.log('canceled');
                        setIsProcessing(false);
                        break;
                    }
                    case 'success':
                        checkFinalStatus()
                            .then((res) => {
                                if (res.data?.data?.message?.status === 'error') {
                                    console.error(res.data?.data?.message);
                                    onPaymentResult({ success: false, data: { code: 'internal' } });
                                    return;
                                }

                                onPaymentResult({ success: true, data: res.data?.data });
                            })
                            .catch((e) => {
                                console.error(e);
                                onPaymentResult({ success: false, data: { code: 'internal' } });
                            })
                            .finally(() => setIsProcessing(false));
                        break;
                    case 'error':
                        console.error(event);
                        onPaymentResult({ success: false, data: { code: 'PSPError' } });
                        setIsProcessing(false);
                        break;
                    default:
                        console.warn('unexpected event', event.type);
                        setIsProcessing(false);
                }
            });
        },
        [revolutPay, checkFinalStatus, onPaymentResult],
    );

    const paymentOptions = useMemo(
        () => ({
            currency: config.currency,
            totalAmount: config.totalAmount,
            buttonStyle: {
                radius: 'small',
                height: '48px',
                cashback: config.allowPromotionalContent ?? true,
                cashbackCurrency: config.currency,
            },
            mobileRedirectUrls: {
                success: successCallbackUrl,
                failure: failureCallbackUrl,
                cancel: failureCallbackUrl,
            },
            __features: {
                disableGuestSignIn: config.disableGuestSignIn,
                usePopupWithNestedFrames: config.usePopupWithNestedFrames ?? true,
            },
            createOrder: async () => {
                setIsProcessing(true);

                try {
                    const res = await process();
                    return { publicId: res.data.data.action };
                } catch (error) {
                    console.error(error);
                    setIsProcessing(false);
                    let code = 'internal';
                    if (error.response?.data?.data?.code === 'order_is_cancelled') {
                        code = error.response?.data?.data?.code;
                    }
                    onPaymentResult({ success: false, data: { code: code } });
                }
            },
            ...(lineItemName ? {
                lineItems: [
                    {
                        name: lineItemName,
                        totalAmount: config.totalAmount,
                        unitPriceAmount: config.totalAmount,
                    },
                ],
            } : {}),
        }),
        [
            config.allowPromotionalContent,
            config.currency,
            config.totalAmount,
            config.disableGuestSignIn,
            lineItemName,
            successCallbackUrl,
            failureCallbackUrl,
            process,
            onPaymentResult,
            config.usePopupWithNestedFrames,
        ],
    );

    useEffect(
        () => {
            if (root && revolutPay) {
                revolutPay.mount(root, paymentOptions);
                return revolutPay.destroy;
            }
        },
        [root, revolutPay, paymentOptions],
    );

    // load revolut upsell module
    useEffect(
        () => {
            if (config.allowPromotionalContent === false) {
                return;
            }

            RevolutCheckout.upsell({
                locale,
                mode: isSandbox ? 'sandbox' : 'prod',
                publicToken,
            }).then(setRevolutPayUpsell);
        },
        [config.allowPromotionalContent, locale, isSandbox, publicToken],
    );

    // display revolut upsell icon in the header
    useEffect(
        () => {
            if (!revolutPayUpsell || !upsellRoot) {
                return;
            }

            revolutPayUpsell.promotionalBanner.mount(
                upsellRoot,
                {
                    variant: 'banner',
                    currency: config.currency,
                },
            );

            return revolutPayUpsell.destroy;
        },
        [upsellRoot, revolutPayUpsell, config.currency],
    );

    return { isProcessing, isReady: !!revolutPay, setupError };
};
